import { type Locale } from '../i18n'

export type ServiceId = 'storage' | 'pickpack' | 'shipping' | 'returns'

/**
 * Contenu textuel d'une fiche service, par langue. = l'ancien type `ServiceContent`
 * (inline dans `ServicePage.tsx`) MOINS le champ `slug` codé en dur. Les liens croisés
 * `others` référencent désormais un `ServiceId` (l'URL est construite via
 * `localizedPath(SERVICE_PAGEKEY_BY_ID[id], locale)` dans la page).
 */
export type ServiceContent = {
  badge: string
  title: string
  italic: string
  tail: string
  desc: string
  bullets: { title: string; desc: string }[]
  cta: string

  flowSteps: string[]
  flowBadge: string
  flowKicker: string

  stats: { value: string; label: string; caption: string }[]

  features: { title: string; desc: string }[]

  promise: string

  faqs: { q: string; a: string }[]

  others: { id: ServiceId; label: string; desc: string }[]

  // SEO
  metaDescription: string
}

/** Clé de route (pageKey) par identifiant de service. */
export const SERVICE_PAGEKEY_BY_ID = {
  storage: 'service.storage',
  pickpack: 'service.pickpack',
  shipping: 'service.shipping',
  returns: 'service.returns',
} as const

/** Identifiant de service par clé de route (pageKey) — réciproque de la table ci-dessus. */
export const SERVICE_ID_BY_PAGEKEY = {
  'service.storage': 'storage',
  'service.pickpack': 'pickpack',
  'service.shipping': 'shipping',
  'service.returns': 'returns',
} as const

export const SERVICE_CONTENT: Record<Locale, Record<ServiceId, ServiceContent>> = {
  fr: {
    storage: {
      badge: 'Stockage',
      title: 'Un entrepôt européen, ',
      italic: 'sans volume minimum',
      tail: '.',
      desc:
        "Stockez 1 ou 1 000 références dans notre entrepôt situé au cœur de l'Europe. Réception, contrôle qualité, mise en stock et inventaire temps réel — sur racks, en palettes au sol ou en bacs picking, selon vos produits.",
      bullets: [
        { title: 'Racks, palettes & bacs', desc: "Chaque produit à l'emplacement adapté." },
        { title: 'Stock temps réel', desc: 'Synchronisé avec votre boutique.' },
        { title: 'Réception sous 24 h', desc: 'Contrôle qualité inclus.' },
        { title: 'Sécurité 24/7', desc: 'Vidéosurveillance & accès biométrique.' },
      ],
      cta: 'Stocker mes produits',
      flowSteps: ['Réception', 'Contrôle qualité', 'Mise en rack'],
      flowBadge: 'Flux Stockage',
      flowKicker: 'De la palette au rack — temps réel',
      stats: [
        {
          value: '3',
          label: 'Modes de stockage',
          caption: 'Racks dynamiques pour la rotation rapide, palettes au sol pour le volumineux, bacs picking pour les petites références.',
        },
        {
          value: '24 h',
          label: 'Réception sous 24 h',
          caption: 'Contrôle qualité, comptage et mise en rack inclus dès le déchargement.',
        },
        {
          value: '99,9 %',
          label: 'Précision inventaire',
          caption: 'Inventaires hebdomadaires automatisés et consultables en temps réel.',
        },
      ],
      features: [
        {
          title: 'Racks dynamiques',
          desc: "Hauteur de stockage 6 m, gerbeurs et transpalettes électriques pour palettes lourdes.",
        },
        {
          title: 'Sécurité 24/7',
          desc: 'Vidéosurveillance, accès biométrique, scellés inviolables sur cartons sortants.',
        },
        {
          title: 'Température contrôlée',
          desc: 'Entrepôt tempéré 11–18 °C en permanence, hygrométrie surveillée.',
        },
        {
          title: 'Multi-référence',
          desc: 'Du sachet de semence au canapé deux places — toutes catégories acceptées.',
        },
        {
          title: 'Inventaire temps réel',
          desc: 'Synchronisé avec votre boutique. Alertes de seuil bas par e-mail et webhook.',
        },
        {
          title: 'Au cœur de l\'Europe',
          desc: 'Belgique — accès direct aux marchés FR, DE, NL, LU et UK en 24-48 h.',
        },
      ],
      promise: 'Votre stock, rangé, compté et visible en temps réel — comme s’il était chez vous.',
      faqs: [
        {
          q: 'Quelle est la durée minimum de stockage ?',
          a: "Aucune. Vous pouvez stocker une référence pendant 1 jour ou 5 ans — votre stock reste disponible et visible en temps réel, sans engagement.",
        },
        {
          q: 'Acceptez-vous les produits sensibles à la température ?',
          a: "Notre entrepôt est tempéré entre 11 et 18 °C en permanence. Pour le grand froid (vaccins, sérums), nous orientons vers un partenaire spécialisé.",
        },
        {
          q: 'Comment se passe la réception du stock ?',
          a: "Vous nous transmettez bordereau et photos produit. À l'arrivée : contrôle quantité, photos d'intégrité, mise en racks, et mise à jour de votre stock e-commerce sous 24 h ouvrées.",
        },
        {
          q: "Combien coûte le stockage e-commerce en Belgique chez Yaslan ?",
          a: "Chez Yaslan, le stockage n'est pas facturé séparément : vous ne payez que le transport et le pick & pack de chaque commande expédiée. Réception, mise en stock et inventaire sont compris. Le détail est sur notre page Tarifs.",
        },
      ],
      others: [
        { id: 'pickpack', label: 'Pick & Pack', desc: 'Préparation à votre image' },
        { id: 'shipping', label: 'Expédition 24h', desc: 'Cut-off à 15h, livré demain' },
        { id: 'returns', label: 'Retours', desc: 'Portail client en marque blanche' },
      ],
      metaDescription:
        "Stockage e-commerce en Belgique : racks dynamiques, palettes, bacs picking. Entrepôt sécurisé 24/7, tempéré 11-18 °C, réception sous 24 h, inventaire temps réel.",
    },

    pickpack: {
      badge: 'Pick & Pack',
      title: 'Une préparation ',
      italic: 'à votre image',
      tail: '.',
      desc:
        "Cartons brandés, papier de soie, encartage promo, mots manuscrits — chaque détail de votre packaging est sous contrôle. Préparation en moins de 4 h pour les commandes prioritaires.",
      bullets: [
        { title: 'Branding sur mesure', desc: 'Cartons, scotch, papier de soie.' },
        { title: 'Encartage promo', desc: 'Flyers, échantillons, codes promo.' },
        { title: 'Préparation < 4 h', desc: 'Pour les commandes prioritaires.' },
        { title: "Taux d'erreur < 0,1 %", desc: 'Double-vérification scan.' },
      ],
      cta: 'Personnaliser mon pack',
      flowSteps: ['Picking guidé', 'Double-scan', 'Emballage signature'],
      flowBadge: 'Flux Pick & Pack',
      flowKicker: 'De la commande à l\'unboxing',
      stats: [
        {
          value: '< 0,1 %',
          label: "Taux d'erreur",
          caption: 'Double-vérification scan SKU + scan colis avant fermeture.',
        },
        {
          value: '< 4 h',
          label: 'Préparation prioritaire',
          caption: 'Pour les commandes flaggées prioritaires : préparées et expédiées en 4 h chrono.',
        },
        {
          value: '100 %',
          label: 'Branding préservé',
          caption: 'Cartons, papier de soie, étiquettes, ruban — tout reste à votre marque.',
        },
      ],
      features: [
        {
          title: 'Cartons brandés',
          desc: 'Format custom, impression intérieure et extérieure, papier kraft signé en option.',
        },
        {
          title: 'Encartage promo',
          desc: 'Flyers, échantillons, codes promo, mot personnalisé inséré à la commande.',
        },
        {
          title: 'Mots manuscrits',
          desc: 'Disponibles à partir d\'un seuil de panier. Idéal pour la mode et la cosmétique.',
        },
        {
          title: 'Kits assemblés',
          desc: 'Coffrets multi-produits préparés à la commande, en îlot dédié pendant les pics.',
        },
        {
          title: 'Calage anti-choc',
          desc: 'Mousse moulée, papier bulle, double cartonnage sur articles fragiles.',
        },
        {
          title: 'Photos qualité',
          desc: 'Photos systématiques avant fermeture sur commandes >100 € (option activable).',
        },
      ],
      promise: 'Chaque colis traité comme s\'il était le seul.',
      faqs: [
        {
          q: 'Puis-je fournir mes propres cartons ?',
          a: "Oui — vous nous envoyez vos cartons brandés et nous les utilisons en priorité. Nous gardons un stock de cartons neutres en backup.",
        },
        {
          q: "À partir de quel volume puis-je personnaliser le packaging ?",
          a: "Aucun volume minimum. Dès le premier colis vous pouvez nous fournir cartons, papier, stickers, mots — nous adaptons sans frais de mise en route.",
        },
        {
          q: 'Comment fonctionne l\'encartage promo ?',
          a: "Vous nous envoyez vos flyers/échantillons. Nous les ajoutons selon vos règles : à toute commande, au-dessus d'un seuil panier, sur SKUs spécifiques, ou sur segments clients.",
        },
        {
          q: 'Acceptez-vous les commandes urgentes ?',
          a: "Oui — toute commande passée avant 15 h en standard est expédiée le jour même. Pour les commandes flaggées priorité, nous garantissons une préparation en moins de 4 h ouvrées.",
        },
      ],
      others: [
        { id: 'storage', label: 'Stockage', desc: 'Entrepôt européen flexible' },
        { id: 'shipping', label: 'Expédition 24h', desc: 'Cut-off à 15h, livré demain' },
        { id: 'returns', label: 'Retours', desc: 'Portail client en marque blanche' },
      ],
      metaDescription:
        "Pick & Pack e-commerce à votre image : packaging brandé, encartage promo, préparation < 4 h, taux d'erreur < 0,1 %. Pour marques exigeantes.",
    },

    shipping: {
      badge: 'Expédition 24h',
      title: 'Cut-off à 15h, ',
      italic: 'livré demain',
      tail: '.',
      desc:
        "Toute commande passée avant 15 h est expédiée le jour même. Notre routing intelligent compare 30+ transporteurs et choisit l'option optimale entre prix, délai et zone de livraison.",
      bullets: [
        { title: 'Routing intelligent', desc: '30+ transporteurs comparés en temps réel.' },
        { title: 'Tracking unifié', desc: 'Un seul ID, peu importe le transporteur.' },
        { title: 'Tarifs mutualisés', desc: 'Volume groupé répercuté sur vos prix.' },
        { title: 'Livraison Europe', desc: '24h-72h selon la zone.' },
      ],
      cta: "Activer l'expédition 24h",
      flowSteps: ['Tri par zone', 'Étiquetage transporteur', 'Remise transporteur'],
      flowBadge: 'Flux Expédition',
      flowKicker: 'Du cut-off à la remise transporteur',
      stats: [
        {
          value: '15 h',
          label: 'Cut-off quotidien',
          caption: 'Toute commande passée avant 15 h est expédiée le jour même, du lundi au vendredi.',
        },
        {
          value: '30+',
          label: 'Transporteurs intégrés',
          caption: 'bpost, DHL, UPS, FedEx, GLS, DPD, Mondial Relay, et bien d\'autres.',
        },
        {
          value: 'Volume',
          label: 'Tarifs mutualisés',
          caption: "Vos volumes s'ajoutent à ceux de nos clients pour accéder à des grilles transporteurs plus avantageuses qu'en direct.",
        },
      ],
      features: [
        {
          title: 'Routing intelligent',
          desc: '30+ transporteurs comparés en temps réel selon vos règles : prix, délai, zone, mode (domicile, relais, express).',
        },
        {
          title: 'Tracking unifié',
          desc: "Un seul ID Yaslan pour vos clients, peu importe le transporteur. Notifications e-mail/SMS automatiques.",
        },
        {
          title: 'Failover transporteur',
          desc: "Si un transporteur tombe ou retarde, bascule automatique vers un transporteur de secours en moins d'1 seconde.",
        },
        {
          title: 'Livraison Europe',
          desc: '24 h en Belgique et France ; 48-72 h dans le reste de l\'UE selon la zone.',
        },
        {
          title: 'Documentation douane',
          desc: 'Préparation des documents douaniers pour expéditions UK, Suisse, Norvège et international.',
        },
        {
          title: 'Reporting transporteur',
          desc: "Vue analytics par transporteur : taux à temps, taux d'incident, coût moyen, zones critiques.",
        },
      ],
      promise: 'Notre routing choisit. Votre marge en profite.',
      faqs: [
        {
          q: 'Que se passe-t-il pour les commandes passées après 15 h ?',
          a: "Elles sont préparées dans la nuit et expédiées le lendemain matin (avant 11 h). Vos clients reçoivent leur colis en J+2 standard, J+1 si zone proche.",
        },
        {
          q: 'Puis-je imposer un transporteur en particulier ?',
          a: "Oui — vous pouvez forcer un transporteur par défaut, par mode (domicile/relais), par zone, ou laisser notre routing optimiser automatiquement.",
        },
        {
          q: "Comment gérez-vous les commandes urgentes (J+1 garantie) ?",
          a: "Option express activable au panier ou au niveau commande. Routing automatique vers DHL Express, UPS Saver ou FedEx Priority selon zone et délai.",
        },
        {
          q: "Quels transporteurs sont activés par défaut ?",
          a: "bpost, GLS, DPD, DHL, UPS, FedEx, Mondial Relay et PostNL sont disponibles dès l'ouverture de votre compte. D'autres transporteurs européens peuvent être ajoutés sur demande selon vos volumes et vos zones cibles.",
        },
      ],
      others: [
        { id: 'storage', label: 'Stockage', desc: 'Entrepôt européen flexible' },
        { id: 'pickpack', label: 'Pick & Pack', desc: 'Préparation à votre image' },
        { id: 'returns', label: 'Retours', desc: 'Portail client en marque blanche' },
      ],
      metaDescription:
        "Tarifs d'expédition e-commerce négociés : 30+ transporteurs comparés, cut-off 15 h, livraison 24 h en Europe. Sans abonnement, tracking unifié.",
    },

    returns: {
      badge: 'Retours',
      title: 'Un retour, ',
      italic: 'zéro friction',
      tail: '.',
      desc:
        "Portail client en marque blanche, règles de retour personnalisables, remise en stock sous 24 h. Vos clients sont rassurés, vos équipes ne perdent plus de temps.",
      bullets: [
        { title: 'Portail marque blanche', desc: 'À votre nom de domaine.' },
        { title: 'Règles personnalisables', desc: 'Délai, motifs, remboursement.' },
        { title: 'Remise en stock 24 h', desc: 'Contrôle qualité inclus.' },
        { title: 'Analytics dédié', desc: 'Identifiez les motifs récurrents.' },
      ],
      cta: 'Configurer les retours',
      flowSteps: ['Portail client', 'Réception & tri', 'Remise en stock'],
      flowBadge: 'Flux Retours',
      flowKicker: 'Du portail à la revente',
      stats: [
        {
          value: '24 h',
          label: 'Remise en stock',
          caption: 'Vos articles redeviennent vendables sous un jour ouvré après réception.',
        },
        {
          value: '100 %',
          label: 'Portail marque blanche',
          caption: 'À votre nom de domaine, à vos couleurs, sans logo Yaslan visible.',
        },
        {
          value: '0',
          label: 'Frais cachés',
          caption: "Tarif clair par retour traité — vous savez ce que vous payez avant de l'engager.",
        },
      ],
      features: [
        {
          title: 'Portail marque blanche',
          desc: "À votre nom de domaine. Vos clients ne voient jamais Yaslan — seulement votre marque.",
        },
        {
          title: 'Règles personnalisables',
          desc: 'Délai (14, 30, 60 j…), motifs autorisés, remboursement automatique ou validation manuelle.',
        },
        {
          title: 'Tri en réception',
          desc: 'Photos systématiques. Classement : revendable, déclassé, casse documentée. Vous décidez du sort.',
        },
        {
          title: 'Remise en stock < 24 h',
          desc: "Articles revendables remis en rayon dans la journée. Stock disponible immédiatement à la vente.",
        },
        {
          title: 'Échanges directs',
          desc: 'Le client peut demander un échange direct (taille, couleur) sans repasser par votre site.',
        },
        {
          title: 'Analytics retours',
          desc: 'Tableau de bord par produit, par motif, par zone — pour identifier vos points faibles.',
        },
      ],
      promise: 'Le retour devient une expérience. Pas un coût.',
      faqs: [
        {
          q: "Le portail de retours peut-il vraiment porter ma marque ?",
          a: "Oui, à 100 %. Vous fournissez votre logo, vos couleurs, votre domaine (retours.votremarque.com). Les e-mails de notification sont également à votre marque.",
        },
        {
          q: 'Combien coûte un retour ?',
          a: "Tarif unique par retour traité, tout inclus : étiquette, réception, contrôle qualité, photos, remise en stock ou destruction documentée. Pas de surprise.",
        },
        {
          q: "Que devient le stock retourné endommagé ?",
          a: "Trois options à votre main : isolation pour revente seconde main, déclassement avec décote, ou destruction documentée. Photos et fiche par article.",
        },
        {
          q: 'Puis-je proposer le retour gratuit à mes clients ?',
          a: "Oui — vous décidez qui paie l'étiquette retour : vous, le client, ou conditionnellement (gratuit au-dessus d'un seuil panier). Configurable au niveau règle.",
        },
      ],
      others: [
        { id: 'storage', label: 'Stockage', desc: 'Entrepôt européen flexible' },
        { id: 'pickpack', label: 'Pick & Pack', desc: 'Préparation à votre image' },
        { id: 'shipping', label: 'Expédition 24h', desc: 'Cut-off à 15h, livré demain' },
      ],
      metaDescription:
        "Gestion des retours e-commerce : portail marque blanche, règles personnalisables, remise en stock 24 h, analytics. Aucun frais caché.",
    },
  },
  en: {
    storage: {
      badge: 'Storage',
      title: 'A European warehouse, ',
      italic: 'with no minimum volume',
      tail: '.',
      desc:
        'Store 1 or 1,000 references in our warehouse at the heart of Europe. Receiving, quality control, putaway and real-time inventory — on racks, floor pallets or picking bins, depending on your products.',
      bullets: [
        { title: 'Racks, pallets & bins', desc: 'Every product in the right location.' },
        { title: 'Real-time stock', desc: 'Synced with your store.' },
        { title: 'Receiving within 24h', desc: 'Quality control included.' },
        { title: '24/7 security', desc: 'Video surveillance & biometric access.' },
      ],
      cta: 'Store my products',
      flowSteps: ['Receiving', 'Quality control', 'Racking'],
      flowBadge: 'Storage flow',
      flowKicker: 'From pallet to rack — in real time',
      stats: [
        {
          value: '3',
          label: 'Storage methods',
          caption: 'Dynamic racking for fast movers, floor pallets for bulky goods, picking bins for small references.',
        },
        {
          value: '24h',
          label: 'Receiving within 24h',
          caption: 'Quality control, counting and racking included from unloading.',
        },
        {
          value: '99.9%',
          label: 'Inventory accuracy',
          caption: 'Automated weekly inventories, viewable in real time.',
        },
      ],
      features: [
        {
          title: 'Dynamic racking',
          desc: '6 m storage height, electric stackers and pallet trucks for heavy pallets.',
        },
        {
          title: '24/7 security',
          desc: 'Video surveillance, biometric access, tamper-proof seals on outbound boxes.',
        },
        {
          title: 'Temperature controlled',
          desc: 'Warehouse kept at a constant 11–18°C, with humidity monitored.',
        },
        {
          title: 'Multi-reference',
          desc: 'From a packet of seeds to a two-seater sofa — all categories accepted.',
        },
        {
          title: 'Real-time inventory',
          desc: 'Synced with your store. Low-stock threshold alerts by email and webhook.',
        },
        {
          title: 'At the heart of Europe',
          desc: 'Belgium — direct access to the FR, DE, NL, LU and UK markets in 24-48h.',
        },
      ],
      promise: 'Your stock: stored, counted and visible in real time — as if it were at your place.',
      faqs: [
        {
          q: 'What is the minimum storage duration?',
          a: 'None. You can store a reference for 1 day or 5 years — your stock stays available and visible in real time, with no commitment.',
        },
        {
          q: 'Do you accept temperature-sensitive products?',
          a: 'Our warehouse is kept at a constant 11 to 18°C. For deep cold (vaccines, serums), we refer you to a specialist partner.',
        },
        {
          q: 'How does stock receiving work?',
          a: 'You send us the delivery note and product photos. On arrival: quantity check, integrity photos, racking, and an update of your e-commerce stock within 24 working hours.',
        },
        {
          q: 'How much does e-commerce storage in Belgium cost with Yaslan?',
          a: 'At Yaslan, storage is not billed separately: you only pay shipping and pick & pack on each shipped order. Receiving, putaway and inventory are included. Details on our Pricing page.',
        },
      ],
      others: [
        { id: 'pickpack', label: 'Pick & Pack', desc: 'Fulfilment in your image' },
        { id: 'shipping', label: '24h shipping', desc: '3pm cut-off, delivered tomorrow' },
        { id: 'returns', label: 'Returns', desc: 'White-label customer portal' },
      ],
      metaDescription:
        'E-commerce storage in Belgium: dynamic racking, pallets, picking bins. Secure 24/7 warehouse at 11–18°C, receiving within 24h, real-time inventory.',
    },

    pickpack: {
      badge: 'Pick & Pack',
      title: 'Fulfilment ',
      italic: 'in your image',
      tail: '.',
      desc:
        'Branded boxes, tissue paper, promo inserts, handwritten notes — every detail of your packaging is under control. Prepared in under 4h for priority orders.',
      bullets: [
        { title: 'Tailored branding', desc: 'Boxes, tape, tissue paper.' },
        { title: 'Promo inserts', desc: 'Flyers, samples, promo codes.' },
        { title: 'Prepared in <4h', desc: 'For priority orders.' },
        { title: 'Error rate <0.1%', desc: 'Double-check scanning.' },
      ],
      cta: 'Customise my pack',
      flowSteps: ['Guided picking', 'Double scan', 'Signature packing'],
      flowBadge: 'Pick & Pack flow',
      flowKicker: 'From order to unboxing',
      stats: [
        {
          value: '<0.1%',
          label: 'Error rate',
          caption: 'Double-check scanning: SKU scan + parcel scan before sealing.',
        },
        {
          value: '<4h',
          label: 'Priority preparation',
          caption: 'For orders flagged as priority: prepared and shipped in 4h flat.',
        },
        {
          value: '100%',
          label: 'Branding preserved',
          caption: 'Boxes, tissue paper, labels, ribbon — everything stays on your brand.',
        },
      ],
      features: [
        {
          title: 'Branded boxes',
          desc: 'Custom format, inside and outside printing, signed kraft paper as an option.',
        },
        {
          title: 'Promo inserts',
          desc: 'Flyers, samples, promo codes, personalised note added to the order.',
        },
        {
          title: 'Handwritten notes',
          desc: 'Available above a basket threshold. Ideal for fashion and cosmetics.',
        },
        {
          title: 'Assembled kits',
          desc: 'Multi-product gift sets prepared to order, in a dedicated cell during peaks.',
        },
        {
          title: 'Shock-proof cushioning',
          desc: 'Moulded foam, bubble wrap, double boxing on fragile items.',
        },
        {
          title: 'Quality photos',
          desc: 'Systematic photos before sealing on orders >€100 (activatable option).',
        },
      ],
      promise: 'Every parcel treated as if it were the only one.',
      faqs: [
        {
          q: 'Can I supply my own boxes?',
          a: 'Yes — you send us your branded boxes and we use them as a priority. We keep a stock of plain boxes as backup.',
        },
        {
          q: 'From what volume can I customise the packaging?',
          a: 'No minimum volume. From the very first parcel you can supply us with boxes, paper, stickers and notes — we adapt with no set-up fee.',
        },
        {
          q: 'How do promo inserts work?',
          a: 'You send us your flyers/samples. We add them according to your rules: to every order, above a basket threshold, on specific SKUs, or on customer segments.',
        },
        {
          q: 'Do you accept urgent orders?',
          a: 'Yes — every standard order placed before 3pm ships the same day. For orders flagged as priority, we guarantee preparation in under 4 working hours.',
        },
      ],
      others: [
        { id: 'storage', label: 'Storage', desc: 'Flexible European warehouse' },
        { id: 'shipping', label: '24h shipping', desc: '3pm cut-off, delivered tomorrow' },
        { id: 'returns', label: 'Returns', desc: 'White-label customer portal' },
      ],
      metaDescription:
        'E-commerce Pick & Pack in your image: branded packaging, promo inserts, prepared in <4h, error rate <0.1%. For demanding brands.',
    },

    shipping: {
      badge: '24h shipping',
      title: '3pm cut-off, ',
      italic: 'delivered tomorrow',
      tail: '.',
      desc:
        'Every order placed before 3pm ships the same day. Our smart routing compares 30+ carriers and picks the optimal option across price, lead time and delivery zone.',
      bullets: [
        { title: 'Smart routing', desc: '30+ carriers compared in real time.' },
        { title: 'Unified tracking', desc: 'One ID, whatever the carrier.' },
        { title: 'Pooled rates', desc: 'Grouped volume passed on to your prices.' },
        { title: 'Europe-wide delivery', desc: '24h-72h depending on the zone.' },
      ],
      cta: 'Activate 24h shipping',
      flowSteps: ['Sorting by zone', 'Carrier labelling', 'Carrier handover'],
      flowBadge: 'Shipping flow',
      flowKicker: 'From cut-off to carrier handover',
      stats: [
        {
          value: '3pm',
          label: 'Daily cut-off',
          caption: 'Every order placed before 3pm ships the same day, Monday to Friday.',
        },
        {
          value: '30+',
          label: 'Integrated carriers',
          caption: 'bpost, DHL, UPS, FedEx, GLS, DPD, Mondial Relay, and many more.',
        },
        {
          value: 'Volume',
          label: 'Pooled rates',
          caption: 'Your volumes add to those of our clients to access carrier rate cards more favourable than direct.',
        },
      ],
      features: [
        {
          title: 'Smart routing',
          desc: '30+ carriers compared in real time according to your rules: price, lead time, zone, mode (home, pickup point, express).',
        },
        {
          title: 'Unified tracking',
          desc: 'A single Yaslan ID for your customers, whatever the carrier. Automatic email/SMS notifications.',
        },
        {
          title: 'Carrier failover',
          desc: 'If a carrier goes down or runs late, automatic switch-over to a backup carrier in under 1 second.',
        },
        {
          title: 'Europe-wide delivery',
          desc: '24h in Belgium and France; 48-72h in the rest of the EU depending on the zone.',
        },
        {
          title: 'Customs documentation',
          desc: 'Preparation of customs documents for shipments to the UK, Switzerland, Norway and international.',
        },
        {
          title: 'Carrier reporting',
          desc: 'Analytics view by carrier: on-time rate, incident rate, average cost, critical zones.',
        },
      ],
      promise: 'Our routing chooses. Your margin benefits.',
      faqs: [
        {
          q: 'What happens to orders placed after 3pm?',
          a: 'They are prepared overnight and shipped the next morning (before 11am). Your customers receive their parcel in 2 working days as standard, or next day for nearby zones.',
        },
        {
          q: 'Can I impose a specific carrier?',
          a: 'Yes — you can force a default carrier, by mode (home/pickup point), by zone, or let our routing optimise automatically.',
        },
        {
          q: 'How do you handle urgent orders (guaranteed next-day)?',
          a: 'An express option can be activated at the basket or order level. Automatic routing to DHL Express, UPS Saver or FedEx Priority depending on zone and lead time.',
        },
        {
          q: 'Which carriers are enabled by default?',
          a: 'bpost, GLS, DPD, DHL, UPS, FedEx, Mondial Relay and PostNL are available as soon as your account opens. Other European carriers can be added on request, depending on your volumes and target zones.',
        },
      ],
      others: [
        { id: 'storage', label: 'Storage', desc: 'Flexible European warehouse' },
        { id: 'pickpack', label: 'Pick & Pack', desc: 'Fulfilment in your image' },
        { id: 'returns', label: 'Returns', desc: 'White-label customer portal' },
      ],
      metaDescription:
        'E-commerce shipping rates, negotiated: 30+ carriers compared, 3pm cut-off, 24h delivery across Europe. No subscription, unified tracking.',
    },

    returns: {
      badge: 'Returns',
      title: 'A return, ',
      italic: 'zero friction',
      tail: '.',
      desc:
        'White-label customer portal, customisable return rules, restocking within 24h. Your customers are reassured, your teams stop wasting time.',
      bullets: [
        { title: 'White-label portal', desc: 'On your own domain name.' },
        { title: 'Customisable rules', desc: 'Window, reasons, refund.' },
        { title: 'Restocking in 24h', desc: 'Quality control included.' },
        { title: 'Dedicated analytics', desc: 'Spot recurring reasons.' },
      ],
      cta: 'Configure returns',
      flowSteps: ['Customer portal', 'Receiving & sorting', 'Restocking'],
      flowBadge: 'Returns flow',
      flowKicker: 'From portal to resale',
      stats: [
        {
          value: '24h',
          label: 'Restocking',
          caption: 'Your items become sellable again within one working day of receipt.',
        },
        {
          value: '100%',
          label: 'White-label portal',
          caption: 'On your own domain name, in your colours, with no visible Yaslan logo.',
        },
        {
          value: '0',
          label: 'Hidden fees',
          caption: 'A clear rate per return processed — you know what you pay before committing.',
        },
      ],
      features: [
        {
          title: 'White-label portal',
          desc: 'On your own domain name. Your customers never see Yaslan — only your brand.',
        },
        {
          title: 'Customisable rules',
          desc: 'Window (14, 30, 60 days…), permitted reasons, automatic refund or manual validation.',
        },
        {
          title: 'Sorting on receipt',
          desc: 'Systematic photos. Classification: sellable, downgraded, documented damage. You decide the outcome.',
        },
        {
          title: 'Restocking <24h',
          desc: 'Sellable items put back on the shelf within the day. Stock available for sale immediately.',
        },
        {
          title: 'Direct exchanges',
          desc: 'The customer can request a direct exchange (size, colour) without going back through your site.',
        },
        {
          title: 'Returns analytics',
          desc: 'Dashboard by product, by reason, by zone — to identify your weak points.',
        },
      ],
      promise: 'Returns become an experience. Not a cost.',
      faqs: [
        {
          q: 'Can the returns portal really carry my brand?',
          a: 'Yes, 100%. You provide your logo, your colours, your domain (returns.yourbrand.com). The notification emails are on your brand too.',
        },
        {
          q: 'How much does a return cost?',
          a: 'A single rate per return processed, all included: label, receiving, quality control, photos, restocking or documented destruction. No surprises.',
        },
        {
          q: 'What happens to returned damaged stock?',
          a: 'Three options in your hands: isolation for second-hand resale, downgrading with a markdown, or documented destruction. Photos and a sheet for each item.',
        },
        {
          q: 'Can I offer free returns to my customers?',
          a: 'Yes — you decide who pays for the return label: you, the customer, or conditionally (free above a basket threshold). Configurable at rule level.',
        },
      ],
      others: [
        { id: 'storage', label: 'Storage', desc: 'Flexible European warehouse' },
        { id: 'pickpack', label: 'Pick & Pack', desc: 'Fulfilment in your image' },
        { id: 'shipping', label: '24h shipping', desc: '3pm cut-off, delivered tomorrow' },
      ],
      metaDescription:
        'E-commerce returns management: white-label portal, customisable rules, restocking in 24h, analytics. No hidden fees.',
    },
  } satisfies Record<ServiceId, ServiceContent>, // Task 7
  nl: {
    storage: {
      badge: 'Opslag',
      title: 'Een Europees magazijn, ',
      italic: 'zonder minimumvolume',
      tail: '.',
      desc:
        'Sla 1 of 1.000 referenties op in ons magazijn in het hart van Europa. Ontvangst, kwaliteitscontrole, inslag en realtime voorraad — in rekken, op vloerpallets of in pickbakken, volgens je producten.',
      bullets: [
        { title: 'Rekken, pallets & bakken', desc: 'Elk product op de juiste plek.' },
        { title: 'Realtime voorraad', desc: 'Gesynchroniseerd met je webshop.' },
        { title: 'Ontvangst binnen 24u', desc: 'Kwaliteitscontrole inbegrepen.' },
        { title: 'Beveiliging 24/7', desc: 'Videobewaking & biometrische toegang.' },
      ],
      cta: 'Mijn producten opslaan',
      flowSteps: ['Ontvangst', 'Kwaliteitscontrole', 'Inslag in rek'],
      flowBadge: 'Opslagflow',
      flowKicker: 'Van pallet tot rek — realtime',
      stats: [
        {
          value: '3',
          label: 'Opslagmethodes',
          caption: 'Dynamische rekken voor snelle rotatie, vloerpallets voor volumineuze goederen, pickbakken voor kleine referenties.',
        },
        {
          value: '24u',
          label: 'Ontvangst binnen 24u',
          caption: 'Kwaliteitscontrole, telling en inslag in rek inbegrepen vanaf het lossen.',
        },
        {
          value: '99,9 %',
          label: 'Voorraadnauwkeurigheid',
          caption: 'Wekelijkse geautomatiseerde inventarissen, realtime te raadplegen.',
        },
      ],
      features: [
        {
          title: 'Dynamische rekken',
          desc: 'Opslaghoogte 6 m, elektrische stapelaars en palletwagens voor zware pallets.',
        },
        {
          title: 'Beveiliging 24/7',
          desc: 'Videobewaking, biometrische toegang, onschendbare zegels op uitgaande dozen.',
        },
        {
          title: 'Temperatuurgecontroleerd',
          desc: 'Magazijn permanent op 11–18 °C, vochtigheid bewaakt.',
        },
        {
          title: 'Multireferentie',
          desc: 'Van een zakje zaden tot een tweezitsbank — alle categorieën aanvaard.',
        },
        {
          title: 'Realtime voorraad',
          desc: 'Gesynchroniseerd met je webshop. Waarschuwingen bij lage voorraaddrempel per e-mail en webhook.',
        },
        {
          title: 'In het hart van Europa',
          desc: 'België — directe toegang tot de markten FR, DE, NL, LU en UK in 24-48u.',
        },
      ],
      promise: 'Jouw voorraad: opgeborgen, geteld en realtime zichtbaar — alsof die bij jou stond.',
      faqs: [
        {
          q: 'Wat is de minimale opslagduur?',
          a: 'Geen enkele. Je kunt een referentie 1 dag of 5 jaar opslaan — je voorraad blijft beschikbaar en realtime zichtbaar, zonder verbintenis.',
        },
        {
          q: 'Aanvaarden jullie temperatuurgevoelige producten?',
          a: 'Ons magazijn wordt permanent tussen 11 en 18 °C gehouden. Voor extreme koude (vaccins, serums) verwijzen we je door naar een gespecialiseerde partner.',
        },
        {
          q: 'Hoe verloopt de ontvangst van de voorraad?',
          a: 'Je bezorgt ons de leveringsbon en productfoto’s. Bij aankomst: controle van het aantal, foto’s van de integriteit, inslag in rekken en bijwerking van je e-commercevoorraad binnen 24 werkuren.',
        },
        {
          q: 'Hoeveel kost e-commerceopslag in België bij Yaslan?',
          a: 'Bij Yaslan wordt opslag niet apart gefactureerd: je betaalt enkel het transport en de pick & pack van elke verzonden bestelling. Ontvangst, inslag en inventaris zijn inbegrepen. Alle details vind je op onze Tarieven-pagina.',
        },
      ],
      others: [
        { id: 'pickpack', label: 'Pick & Pack', desc: 'Verwerking met jouw uitstraling' },
        { id: 'shipping', label: 'Verzending 24u', desc: 'Cut-off om 15u, morgen geleverd' },
        { id: 'returns', label: 'Retourzendingen', desc: 'Klantportaal in white-label' },
      ],
      metaDescription:
        'E-commerce opslag in België: dynamische rekken, pallets, pickbakken. Beveiligd magazijn 24/7 op 11–18 °C, ontvangst binnen 24u, realtime voorraad.',
    },

    pickpack: {
      badge: 'Pick & Pack',
      title: 'Een verwerking ',
      italic: 'met jouw uitstraling',
      tail: '.',
      desc:
        'Gemerkte dozen, zijdepapier, promo-inserts, handgeschreven kaartjes — elk detail van je verpakking is onder controle. Verwerking in minder dan 4u voor prioritaire bestellingen.',
      bullets: [
        { title: 'Branding op maat', desc: 'Dozen, tape, zijdepapier.' },
        { title: 'Promo-inserts', desc: 'Flyers, stalen, promocodes.' },
        { title: 'Verwerking < 4u', desc: 'Voor prioritaire bestellingen.' },
        { title: 'Foutmarge < 0,1 %', desc: 'Dubbele scancontrole.' },
      ],
      cta: 'Mijn pack personaliseren',
      flowSteps: ['Begeleide picking', 'Dubbele scan', 'Kenmerkende verpakking'],
      flowBadge: 'Pick & Pack-flow',
      flowKicker: 'Van bestelling tot unboxing',
      stats: [
        {
          value: '< 0,1 %',
          label: 'Foutmarge',
          caption: 'Dubbele scancontrole: SKU-scan + pakketscan vóór het sluiten.',
        },
        {
          value: '< 4u',
          label: 'Prioritaire verwerking',
          caption: 'Voor als prioritair gemarkeerde bestellingen: verwerkt en verzonden in 4u tijd.',
        },
        {
          value: '100 %',
          label: 'Branding behouden',
          caption: 'Dozen, zijdepapier, etiketten, lint — alles blijft van jouw merk.',
        },
      ],
      features: [
        {
          title: 'Gemerkte dozen',
          desc: 'Maatformaat, bedrukking binnen- en buitenkant, gemerkt kraftpapier als optie.',
        },
        {
          title: 'Promo-inserts',
          desc: 'Flyers, stalen, promocodes, gepersonaliseerd kaartje toegevoegd aan de bestelling.',
        },
        {
          title: 'Handgeschreven kaartjes',
          desc: 'Beschikbaar vanaf een winkelmanddrempel. Ideaal voor mode en cosmetica.',
        },
        {
          title: 'Samengestelde kits',
          desc: 'Geschenksets met meerdere producten op bestelling samengesteld, in een toegewijde cel tijdens pieken.',
        },
        {
          title: 'Schokbestendige opvulling',
          desc: 'Gevormd schuim, bubbeltjesfolie, dubbele verdozing op breekbare artikelen.',
        },
        {
          title: 'Kwaliteitsfoto’s',
          desc: 'Systematische foto’s vóór het sluiten bij bestellingen > € 100 (activeerbare optie).',
        },
      ],
      promise: 'Elk pakket behandeld alsof het het enige was.',
      faqs: [
        {
          q: 'Kan ik mijn eigen dozen aanleveren?',
          a: 'Ja — je bezorgt ons je gemerkte dozen en wij gebruiken die bij voorrang. We houden een voorraad neutrale dozen achter de hand als back-up.',
        },
        {
          q: 'Vanaf welk volume kan ik de verpakking personaliseren?',
          a: 'Geen minimumvolume. Vanaf het eerste pakket kun je ons dozen, papier, stickers en kaartjes aanleveren — we passen ons aan zonder opstartkosten.',
        },
        {
          q: 'Hoe werken promo-inserts?',
          a: 'Je bezorgt ons je flyers/stalen. We voegen ze toe volgens jouw regels: aan elke bestelling, boven een winkelmanddrempel, op specifieke SKU’s of op klantsegmenten.',
        },
        {
          q: 'Aanvaarden jullie spoedbestellingen?',
          a: 'Ja — elke standaardbestelling die vóór 15u wordt geplaatst, wordt dezelfde dag verzonden. Voor als prioritair gemarkeerde bestellingen garanderen we een verwerking in minder dan 4 werkuren.',
        },
      ],
      others: [
        { id: 'storage', label: 'Opslag', desc: 'Flexibel Europees magazijn' },
        { id: 'shipping', label: 'Verzending 24u', desc: 'Cut-off om 15u, morgen geleverd' },
        { id: 'returns', label: 'Retourzendingen', desc: 'Klantportaal in white-label' },
      ],
      metaDescription:
        'E-commerce Pick & Pack met jouw uitstraling: gemerkte verpakking, promo-inserts, verwerking < 4u, foutmarge < 0,1 %. Voor veeleisende merken.',
    },

    shipping: {
      badge: 'Verzending 24u',
      title: 'Cut-off om 15u, ',
      italic: 'morgen geleverd',
      tail: '.',
      desc:
        'Elke bestelling die vóór 15u wordt geplaatst, wordt dezelfde dag verzonden. Onze slimme routing vergelijkt 30+ vervoerders en kiest de optimale optie tussen prijs, levertijd en leverzone.',
      bullets: [
        { title: 'Slimme routing', desc: '30+ vervoerders realtime vergeleken.' },
        { title: 'Uniforme tracking', desc: 'Eén ID, ongeacht de vervoerder.' },
        { title: 'Gebundelde tarieven', desc: 'Gegroepeerd volume doorberekend in je prijzen.' },
        { title: 'Levering in Europa', desc: '24u-72u afhankelijk van de zone.' },
      ],
      cta: 'Verzending 24u activeren',
      flowSteps: ['Sortering per zone', 'Etikettering vervoerder', 'Overdracht aan vervoerder'],
      flowBadge: 'Verzendflow',
      flowKicker: 'Van cut-off tot overdracht aan de vervoerder',
      stats: [
        {
          value: '15u',
          label: 'Dagelijkse cut-off',
          caption: 'Elke bestelling die vóór 15u wordt geplaatst, wordt dezelfde dag verzonden, van maandag tot vrijdag.',
        },
        {
          value: '30+',
          label: 'Geïntegreerde vervoerders',
          caption: 'bpost, DHL, UPS, FedEx, GLS, DPD, Mondial Relay en vele anderen.',
        },
        {
          value: 'Volume',
          label: 'Gebundelde tarieven',
          caption: 'Je volumes worden opgeteld bij die van onze klanten om toegang te krijgen tot voordeligere vervoerderstarieven dan rechtstreeks.',
        },
      ],
      features: [
        {
          title: 'Slimme routing',
          desc: '30+ vervoerders realtime vergeleken volgens jouw regels: prijs, levertijd, zone, modus (thuis, afhaalpunt, express).',
        },
        {
          title: 'Uniforme tracking',
          desc: 'Eén enkel Yaslan-ID voor je klanten, ongeacht de vervoerder. Automatische e-mail-/sms-meldingen.',
        },
        {
          title: 'Failover vervoerder',
          desc: 'Als een vervoerder uitvalt of vertraging oploopt, automatische overschakeling naar een back-upvervoerder in minder dan 1 seconde.',
        },
        {
          title: 'Levering in Europa',
          desc: '24u in België en Frankrijk; 48-72u in de rest van de EU afhankelijk van de zone.',
        },
        {
          title: 'Douanedocumentatie',
          desc: 'Voorbereiding van de douanedocumenten voor zendingen naar UK, Zwitserland, Noorwegen en internationaal.',
        },
        {
          title: 'Rapportage vervoerder',
          desc: 'Analyticsweergave per vervoerder: op-tijd-percentage, incidentpercentage, gemiddelde kost, kritieke zones.',
        },
      ],
      promise: 'Onze routing kiest. Jouw marge profiteert.',
      faqs: [
        {
          q: 'Wat gebeurt er met bestellingen die na 15u worden geplaatst?',
          a: 'Ze worden ’s nachts verwerkt en de volgende ochtend verzonden (vóór 11u). Je klanten ontvangen hun pakket standaard binnen 2 werkdagen, of de volgende dag bij een nabije zone.',
        },
        {
          q: 'Kan ik een specifieke vervoerder opleggen?',
          a: 'Ja — je kunt een standaardvervoerder forceren, per modus (thuis/afhaalpunt), per zone, of onze routing automatisch laten optimaliseren.',
        },
        {
          q: 'Hoe behandelen jullie spoedbestellingen (gegarandeerd de volgende dag)?',
          a: 'Een expressoptie kan op het niveau van de winkelmand of de bestelling worden geactiveerd. Automatische routing naar DHL Express, UPS Saver of FedEx Priority afhankelijk van zone en levertijd.',
        },
        {
          q: 'Welke vervoerders zijn standaard geactiveerd?',
          a: 'bpost, GLS, DPD, DHL, UPS, FedEx, Mondial Relay en PostNL zijn beschikbaar zodra je account wordt geopend. Andere Europese vervoerders kunnen op aanvraag worden toegevoegd, afhankelijk van je volumes en doelzones.',
        },
      ],
      others: [
        { id: 'storage', label: 'Opslag', desc: 'Flexibel Europees magazijn' },
        { id: 'pickpack', label: 'Pick & Pack', desc: 'Verwerking met jouw uitstraling' },
        { id: 'returns', label: 'Retourzendingen', desc: 'Klantportaal in white-label' },
      ],
      metaDescription:
        'E-commerce verzendtarieven, onderhandeld: 30+ vervoerders vergeleken, cut-off 15u, levering binnen 24u in Europa. Geen abonnement, uniforme tracking.',
    },

    returns: {
      badge: 'Retourzendingen',
      title: 'Een retour, ',
      italic: 'zonder frictie',
      tail: '.',
      desc:
        'Klantportaal in white-label, aanpasbare retourregels, herinname op voorraad binnen 24u. Je klanten zijn gerustgesteld, je teams verliezen geen tijd meer.',
      bullets: [
        { title: 'White-label portaal', desc: 'Op je eigen domeinnaam.' },
        { title: 'Aanpasbare regels', desc: 'Termijn, redenen, terugbetaling.' },
        { title: 'Herinname op voorraad 24u', desc: 'Kwaliteitscontrole inbegrepen.' },
        { title: 'Toegewijde analytics', desc: 'Herken terugkerende redenen.' },
      ],
      cta: 'Retourzendingen configureren',
      flowSteps: ['Klantportaal', 'Ontvangst & sortering', 'Herinname op voorraad'],
      flowBadge: 'Retourflow',
      flowKicker: 'Van portaal tot wederverkoop',
      stats: [
        {
          value: '24u',
          label: 'Herinname op voorraad',
          caption: 'Je artikelen worden binnen één werkdag na ontvangst weer verkoopbaar.',
        },
        {
          value: '100 %',
          label: 'White-label portaal',
          caption: 'Op je eigen domeinnaam, in jouw kleuren, zonder zichtbaar Yaslan-logo.',
        },
        {
          value: '0',
          label: 'Verborgen kosten',
          caption: 'Een duidelijk tarief per verwerkte retour — je weet wat je betaalt voordat je het aangaat.',
        },
      ],
      features: [
        {
          title: 'White-label portaal',
          desc: 'Op je eigen domeinnaam. Je klanten zien Yaslan nooit — alleen jouw merk.',
        },
        {
          title: 'Aanpasbare regels',
          desc: 'Termijn (14, 30, 60 dagen…), toegestane redenen, automatische terugbetaling of handmatige validatie.',
        },
        {
          title: 'Sortering bij ontvangst',
          desc: 'Systematische foto’s. Indeling: verkoopbaar, afgewaardeerd, gedocumenteerde breuk. Jij beslist over de bestemming.',
        },
        {
          title: 'Herinname op voorraad < 24u',
          desc: 'Verkoopbare artikelen dezelfde dag terug in het schap. Voorraad onmiddellijk beschikbaar voor verkoop.',
        },
        {
          title: 'Directe omruil',
          desc: 'De klant kan een directe omruil aanvragen (maat, kleur) zonder opnieuw via je site te gaan.',
        },
        {
          title: 'Retouranalytics',
          desc: 'Dashboard per product, per reden, per zone — om je zwakke punten te herkennen.',
        },
      ],
      promise: 'De retour wordt een ervaring. Geen kost.',
      faqs: [
        {
          q: 'Kan het retourportaal echt mijn merk dragen?',
          a: 'Ja, 100 %. Jij levert je logo, je kleuren, je domein (retours.jouwmerk.com). Ook de meldingsmails zijn van jouw merk.',
        },
        {
          q: 'Hoeveel kost een retour?',
          a: 'Eén tarief per verwerkte retour, alles inbegrepen: etiket, ontvangst, kwaliteitscontrole, foto’s, herinname op voorraad of gedocumenteerde vernietiging. Geen verrassingen.',
        },
        {
          q: 'Wat gebeurt er met beschadigde geretourneerde voorraad?',
          a: 'Drie opties in jouw handen: isolatie voor tweedehands wederverkoop, afwaardering met korting, of gedocumenteerde vernietiging. Foto’s en een fiche per artikel.',
        },
        {
          q: 'Kan ik mijn klanten gratis retour aanbieden?',
          a: 'Ja — jij beslist wie het retouretiket betaalt: jij, de klant, of voorwaardelijk (gratis boven een winkelmanddrempel). Configureerbaar op regelniveau.',
        },
      ],
      others: [
        { id: 'storage', label: 'Opslag', desc: 'Flexibel Europees magazijn' },
        { id: 'pickpack', label: 'Pick & Pack', desc: 'Verwerking met jouw uitstraling' },
        { id: 'shipping', label: 'Verzending 24u', desc: 'Cut-off om 15u, morgen geleverd' },
      ],
      metaDescription:
        'Beheer van e-commerceretouren: white-label portaal, aanpasbare regels, herinname op voorraad 24u, analytics. Geen verborgen kosten.',
    },
  } satisfies Record<ServiceId, ServiceContent>, // Task 7
}
